import { FC, useState } from 'react';
import {
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { TDataGridAction } from './DataGridAction';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';

import { CampaignPPCOptions } from '@libs/types';
import { DataGridActionType } from '@carcareers/utils';
import { useSnackbar } from '@carcareers/contexts';

export interface DataGridActionMenuItemProps {
  type?: 'edit' | 'delete' | 'view' | 'other';
  actionType?: DataGridActionType;
  isRecursive?: boolean;
  id?: number;
  row?: any;
}

export const DataGridActionMenuItem: FC<TDataGridAction & DataGridActionMenuItemProps> = ({
  id,
  row,
  label,
  onClick,
  icon,
  type,
  actionType = 'button',
  isRecursive = false,
}) => {
  const [open, setOpen] = useState(false);
  const { toggleSnackbar } = useSnackbar();
  const [value, setValue] = useState<Record<string, string>>({});

  const getIcon = (type?: string) => {
    switch (type) {
      case 'edit':
        return <EditIcon />;
      case 'delete':
        return <DeleteIcon />;
      case 'view':
        return <VisibilityIcon />;
      default:
        return icon;
    }
  };

  const menuIcon = getIcon(type);

  const handleClose = () => setOpen(false);
  const handleDelete = () => {
    onClick(id, row);
    setOpen(false);
  };

  const handleClick = () => {
    if (actionType === 'select') {
      setOpen(true);
    } else {
      onClick(id, row);
    }
  };

  if (actionType === 'text') {
    return (
      <MenuItem>
        <TextField 
          size="small" 
          placeholder={label} 
          onChange={(e) => setValue({ value: e.target.value })}
          value={value.value || ''}
        />
        <Tooltip title="Save" placement="top">
          <Button
            onClick={() => {
              setOpen(false);
              onClick(id, value);
              toggleSnackbar('success', `updated`);
            }}
            variant="text"
            sx={{
              minWidth: 32,
              padding: '4px',
              ml: 1,
            }}
          >
            <CheckIcon fontSize="small" />
          </Button>
        </Tooltip>
      </MenuItem>
    );
  }

  if (actionType === 'select') {
    return (
      <>
        <MenuItem data-testid={`menu-item-${type}-${id}`} onClick={handleClick}>
          {open && (
            <Box sx={{ display: 'flex' }}>
              <Autocomplete
                size="small"
                options={CampaignPPCOptions}
                onChange={(_, value) => setValue(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    sx={{
                      '& .MuiInputBase-root': {
                        padding: '1px 2px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        color: 'text.primary',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.75rem',
                        color: 'text.primary',
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'ellipsis',
                    }}
                  >
                    {option.label}
                  </Box>
                )}
                sx={{
                  width: '100%',
                  minWidth: '125px',
                }}
                disableClearable
              />
              <Tooltip title="Save" placement="top">
                <Button
                  onClick={() => {
                    setOpen(false);
                    onClick(id, value);
                    toggleSnackbar('success', `updated`);
                  }}
                  variant="text"
                  sx={{
                    minWidth: 32,
                    padding: '4px',
                    ml: 1,
                  }}
                >
                  <CheckIcon fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
          )}
          {!open && (
            <>
              <ListItemIcon sx={{ color: type === 'delete' ? 'error.main' : '' }}>{menuIcon}</ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </>
          )}
        </MenuItem>
        {type === 'delete' && (
          <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete this item?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button onClick={handleDelete} autoFocus>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  }

  return (
    <MenuItem data-testid={`menu-item-${type}-${id}`} onClick={handleClick}>
      {menuIcon && <ListItemIcon sx={{ color: type === 'delete' ? 'error.main' : '' }}>{menuIcon}</ListItemIcon>}
      <ListItemText>{label}</ListItemText>
    </MenuItem>
  );
};

export default DataGridActionMenuItem;
